import React from 'react'
import * as R from 'ramda'
import { Accordion } from 'semantic-ui-react'

import Title from 'shared/components/Title'
import useAxios, { mutate } from 'hooks/useAxios'
import IntercomAuthentication from 'components/IntercomAuthentication'
import { IntegrationStatusLabel } from 'components/IntegrationStatusLabel'
import { toast } from 'libs/toast'
import { Loader } from 'shared/components/Loader'
import IntercomContactSyncIntegrationV2Container from 'organisms/IntercomContactSyncIntegrationV2/container'
import IntercomCreateEventIntegrationContainer from 'components/IntercomCreateEventIntegration/container'
import IntercomCreateFollowupConversationIntegration from 'components/IntercomCreateFollowupConversationIntegration/container'
import IntercomPushContactCustomFieldIntegration from 'components/IntercomPushContactCustomFieldIntegration/container'
import useSaveUserActivityLog, { ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import { IntegrationContainer } from 'components/IntegrationContainer'

export default function GlobalIntercomIntegrationContainer() {
  const saveUserActivityLog = useSaveUserActivityLog()

  const intercomCredentialAxiosResult = useAxios({
    url: 'intercom/authentication/',
  })
  const { data: companyIntegrationSettings } = useAxios({
    url: 'account/company-integration-setting/?integration_type=intercom',
  })
  const {
    fetch: updateCompanyIntegrationSettingAPI,
    loading: updateCompanyIntegrationSettingAPILoading,
  } = useAxios({
    method: 'PATCH',
    skip: true,
  })

  if (!companyIntegrationSettings) {
    return <Loader withContainer />
  }

  /**
   *
   * @param {string} name
   * @param {boolean} isActive
   */

  function _saveUserActivityLog(name, requestBody, statusCode) {
    const action = ACTION_NAME.connected_intercom
    const data = {
      integration: name,
      enabled: requestBody.is_active,
    }
    const target = 'company_integration_setting'
    saveUserActivityLog({
      action,
      target,
      statusCode,
      requestBody: data,
    })
  }

  function updateCompanyIntegrationSetting(companyIntegrationSetting, is_active) {
    const data = {
      name: companyIntegrationSetting.name,
      integration_type: companyIntegrationSetting.integration_type,
      is_active,
    }

    updateCompanyIntegrationSettingAPI({
      url: `account/company-integration-setting/`,
      data,
    })
      .then((response) => {
        const toastMessages = response.data.is_active
          ? 'Integration activated!'
          : 'Integration deactivated.'
        toast.success(toastMessages)

        mutate(
          'account/company-integration-setting/?integration_type=intercom',
          (companyIntegrationSettings) => {
            return companyIntegrationSettings.map((setting) =>
              setting.name === response.data.name ? response.data : setting
            )
          },
          true
        )

        _saveUserActivityLog(companyIntegrationSetting.name, data, response.status)
      })
      .catch((error) => {
        toast.error(
          'Oops! Something went wrong. Please try again or contact Simplesat support for help.'
        )
        _saveUserActivityLog(companyIntegrationSetting.name, data, error.response.status)
      })
  }

  const isAuthenticated = Boolean(intercomCredentialAxiosResult.data)

  const companyIntegrationSettingMapper = companyIntegrationSettings.reduce(
    (result, setting) => ({ ...result, [setting.name]: setting }),
    {}
  )

  const pushContactEventSetting = companyIntegrationSettingMapper['push_contact_event']
  const pushFollowUpConversationSetting =
    companyIntegrationSettingMapper['push_follow_up_conversation']
  const pushContactCustomFieldSetting = companyIntegrationSettingMapper['push_contact_custom_field']

  return (
    <React.Fragment>
      <IntegrationContainer>
        <Title title="Intercom" />
        <div className="admin-heading">
          <h2>Intercom</h2>
        </div>
        <div>
          <p>
            Sync contacts, add surveys anywhere in Intercom, and automate follow-up conversations to
            give your team full control and transparency over the customer satisfaction experience.
          </p>
          <p>
            <a
              href="https://help.simplesat.io/en/articles/5230032-intercom-integration"
              target="_blank"
              rel="noreferrer"
            >
              <i className="icon far fa-book" />
              Learn more about integrating with Intercom
            </a>
          </p>

          {/* An extra <div> is needed for the <Segment> to have proper shadow */}
          <div>
            <IntercomAuthentication isAuthenticated={isAuthenticated} />
          </div>

          <hr />

          <h3>Configure integrations</h3>
          <p className="font-bold">Account-wide integrations</p>
          <Accordion
            fluid
            styled
            panels={[
              {
                key: 'Sync Intercom contacts',
                title: {
                  content: (
                    <AccordionTitleContent
                      title="Sync Intercom contacts"
                      active={
                        isAuthenticated &&
                        R.prop('is_enable_customer_sync')(intercomCredentialAxiosResult.data)
                      }
                    />
                  ),
                },
                content: {
                  content: <IntercomContactSyncIntegrationV2Container />,
                },
              },
            ]}
          />

          <p className="mt-6 font-bold">Survey integrations</p>
          <Accordion
            fluid
            styled
            panels={[
              {
                key: 'Create follow-up conversations from feedback',
                title: {
                  content: (
                    <AccordionTitleContent
                      title="Create follow-up conversations from feedback"
                      active={isAuthenticated && pushFollowUpConversationSetting.is_active}
                    />
                  ),
                },
                content: {
                  content: (
                    <IntercomCreateFollowupConversationIntegration
                      isAuthenticated={isAuthenticated}
                      integrationSetting={pushFollowUpConversationSetting}
                      updateCompanyIntegrationSettingAPILoading={
                        updateCompanyIntegrationSettingAPILoading
                      }
                      updateCompanyIntegrationSetting={updateCompanyIntegrationSetting}
                    />
                  ),
                },
              },
              {
                key: 'Create contact events from feedback',
                title: {
                  content: (
                    <AccordionTitleContent
                      title="Create contact events from feedback"
                      active={isAuthenticated && pushContactEventSetting.is_active}
                    />
                  ),
                },
                content: {
                  content: (
                    <IntercomCreateEventIntegrationContainer
                      isAuthenticated={isAuthenticated}
                      integrationSetting={pushContactEventSetting}
                      updateCompanyIntegrationSettingAPILoading={
                        updateCompanyIntegrationSettingAPILoading
                      }
                      updateCompanyIntegrationSetting={updateCompanyIntegrationSetting}
                    />
                  ),
                },
              },
              {
                key: 'Update contact custom attributes from feedback',
                title: {
                  content: (
                    <AccordionTitleContent
                      title="Update contact custom attributes from feedback"
                      active={isAuthenticated && pushContactCustomFieldSetting.is_active}
                    />
                  ),
                },
                content: {
                  content: (
                    <IntercomPushContactCustomFieldIntegration
                      isAuthenticated={isAuthenticated}
                      integrationSetting={pushContactCustomFieldSetting}
                      updateCompanyIntegrationSettingAPILoading={
                        updateCompanyIntegrationSettingAPILoading
                      }
                      updateCompanyIntegrationSetting={updateCompanyIntegrationSetting}
                    />
                  ),
                },
              },
            ]}
          />
        </div>
      </IntegrationContainer>
    </React.Fragment>
  )
}

function AccordionTitleContent({ title, active }) {
  return (
    <React.Fragment>
      {title}
      <span className="ml-4">
        <IntegrationStatusLabel active={active} />
      </span>
    </React.Fragment>
  )
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/GlobalIntercomIntegration/container.js